import { useState, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { message } from '@surecompanies/core_components'
import { toast } from 'react-toastify'

import useFeatureFlags from 'hooks/useFeatureFlags'
import {
  ActivityLogAction,
  postActivityLog,
} from 'modules/activity-log-service'
import {
  reuseVirtualCard,
  createVirtualCard,
  getVirtualCardsForEmployee,
} from 'modules/virtual-cards/actions'
import { VirtualCardType } from 'modules/virtual-cards/constants'
import { confirmMarqetaProvider } from 'modules/virtual-cards/confirmMarqetaProvider'
import { useVcnProviderAvailability } from 'modules/virtual-cards/useVcnProviderAvailability'
import { terminateAndCleanupCards } from 'enrollment-platform/api/vcn'

/**
 * Check if a card is in an active (non-terminated) state.
 * Active states: ACTIVE, SUSPENDED
 * Terminated states: TERMINATED, CLOSED, etc.
 */
const isCardActive = (card) => {
  if (!card) return false
  const state = (card.state || card.status || '').toUpperCase()
  return ['ACTIVE', 'SUSPENDED'].includes(state)
}

/**
 * Custom hook for VCN actions in the legacy Our People section.
 * Handles generate and reuse VCN flows with feature flag support.
 */
const useVcnActionsLegacy = ({
  benefitId,
  employeePublicKey,
  person,
  availableCards,
  selectedCard,
  onGenerateCard,
  currentCard,
  onRefreshCards,
}) => {
  const dispatch = useDispatch()
  const { flags } = useFeatureFlags()
  const providerAvailability = useVcnProviderAvailability()

  // Feature flags for Our People section
  const enableNoteModalBeforeCreate = Boolean(
    flags['admin.virtual-card.enable-note-modal-before-create-our-people']
  )
  const enableTerminateAllBeforeCreate = Boolean(
    flags['admin.virtual-card.enable-terminate-all-before-create-our-people']
  )

  // Modal states
  const [generateModalOpen, setGenerateModalOpen] = useState(false)
  const [pendingProvider, setPendingProvider] = useState(null)
  const [isTerminatingAll, setIsTerminatingAll] = useState(false)

  /**
   * Refresh cards list after any action.
   * Uses callback if provided, otherwise dispatches getVirtualCardsForEmployee.
   */
  const refreshCards = useCallback(() => {
    if (onRefreshCards) {
      onRefreshCards()
    } else if (employeePublicKey) {
      dispatch(getVirtualCardsForEmployee(employeePublicKey))
    }
  }, [dispatch, employeePublicKey, onRefreshCards])

  // Check if current card is active (not terminated)
  const isCurrentCardActive = useMemo(() => {
    if (!currentCard) return false
    // Check if card has pan or token (exists) AND is in active state
    const hasCard = !!(currentCard.pan || currentCard.token)
    if (!hasCard) return false
    return isCardActive(currentCard)
  }, [currentCard])

  // Check if there are any active (non-terminated) cards in available cards
  const hasActiveAvailableCards = useMemo(() => {
    if (!Array.isArray(availableCards) || availableCards.length === 0) {
      return false
    }
    return availableCards.some(isCardActive)
  }, [availableCards])

  // Check if there are any active cards that need to be terminated
  // This is used for showing the warning message
  const hasActiveCardsToTerminate =
    isCurrentCardActive || hasActiveAvailableCards

  // Determine if we need to show the modal
  // Show modal ONLY when terminate-all is enabled AND there are active cards to terminate
  // Note modal alone does NOT trigger popup - it only adds form fields when popup is shown
  const shouldShowModal = useMemo(() => {
    // Only show modal if we need to terminate active cards
    if (enableTerminateAllBeforeCreate && hasActiveCardsToTerminate) return true
    return false
  }, [enableTerminateAllBeforeCreate, hasActiveCardsToTerminate])

  // Should show terminate warning in modal
  const shouldShowTerminateWarning =
    enableTerminateAllBeforeCreate && hasActiveCardsToTerminate

  /**
   * Handle provider selection from dropdown menu.
   * Opens modal only if any flag is enabled, otherwise generates directly.
   */
  const handleProviderSelect = useCallback(
    ({ key }) => {
      const needsMarqetaConfirm =
        key === VirtualCardType.MARQETA &&
        providerAvailability.enabledProviders.length > 1

      // If no modal needed (both flags off or terminate flag on but no active cards)
      // generate directly without popup
      if (!shouldShowModal) {
        if (needsMarqetaConfirm) {
          confirmMarqetaProvider(() => {
            onGenerateCard(VirtualCardType.MARQETA)
          })
        } else {
          onGenerateCard(key)
        }
        return
      }

      // Show modal for confirmation/note
      if (needsMarqetaConfirm) {
        confirmMarqetaProvider(() => {
          setPendingProvider(VirtualCardType.MARQETA)
          setGenerateModalOpen(true)
        })
        return
      }

      setPendingProvider(key)
      setGenerateModalOpen(true)
    },
    [providerAvailability, shouldShowModal, onGenerateCard]
  )

  /**
   * Handle generate VCN confirmation from modal.
   * Terminates existing cards first if feature flag is enabled AND there are active cards.
   * Captures responses from cleanup and generate APIs for activity log.
   */
  const handleGenerateConfirm = useCallback(
    async (activityLogData = {}) => {
      if (!pendingProvider) return

      // Only run terminate-all flow if flag is enabled AND there are ACTIVE cards to terminate
      // If all cards are already terminated, skip cleanup
      const shouldTerminateAll =
        enableTerminateAllBeforeCreate && hasActiveCardsToTerminate

      if (shouldTerminateAll) {
        setIsTerminatingAll(true)
        try {
          // Step 1: Terminate and cleanup all existing cards
          const cleanupResponse = await terminateAndCleanupCards({
            benefitId,
            note: activityLogData.reason,
          })

          // Step 2: Generate new VCN and capture response
          const generateResponse = await dispatch(
            createVirtualCard(benefitId, pendingProvider)
          )

          // Check if any cards were actually terminated
          const terminatedCount = cleanupResponse?.results?.length ?? 0
          const didTerminateCards = terminatedCount > 0

          // Step 3: Post activity log with both responses
          postActivityLog({
            action: ActivityLogAction.GENERATE_VCN,
            description: didTerminateCards
              ? `Generated VCN (${pendingProvider}) for ${person?.FullName} after terminating ${terminatedCount} existing card(s)`
              : `Generated VCN (${pendingProvider}) for ${person?.FullName}`,
            targetUserId: employeePublicKey,
            additionalData: {
              benefitId,
              provider: pendingProvider,
              ...activityLogData,
              // Only set terminatedAllCards: true if cards were actually terminated
              ...(didTerminateCards && {
                terminatedAllCards: true,
                cleanupResponse: {
                  terminatedCount,
                  skippedCount: cleanupResponse?.skipped?.length ?? 0,
                  results: cleanupResponse?.results,
                  skipped: cleanupResponse?.skipped,
                },
              }),
              generateResponse: {
                cardId:
                  generateResponse?.id ?? generateResponse?.card_public_key,
                pan: generateResponse?.pan
                  ? `****${generateResponse.pan.slice(-4)}`
                  : undefined,
                state: generateResponse?.state,
                vcnProvider: generateResponse?.vcnProvider ?? pendingProvider,
                merchantLogId: generateResponse?.merchantLogId,
              },
            },
          })

          setGenerateModalOpen(false)
          setPendingProvider(null)

          // Refresh cards list after successful generate
          refreshCards()
        } catch (error) {
          const errorMessage =
            error?.response?.data?.message ??
            error?.message ??
            'Failed to terminate existing cards or generate new VCN'
          message.error(errorMessage)
        } finally {
          setIsTerminatingAll(false)
        }
      } else {
        // Without terminate-all (flag disabled OR no existing cards), use the original callback
        // but still post activity log if note modal is enabled
        onGenerateCard(pendingProvider)

        if (
          enableNoteModalBeforeCreate &&
          Object.keys(activityLogData).length > 0
        ) {
          postActivityLog({
            action: ActivityLogAction.GENERATE_VCN,
            description: `Generated VCN (${pendingProvider}) for ${person?.FullName}`,
            targetUserId: employeePublicKey,
            additionalData: {
              benefitId,
              provider: pendingProvider,
              ...activityLogData,
            },
          })
        }

        setGenerateModalOpen(false)
        setPendingProvider(null)

        // Refresh cards list after generate
        refreshCards()
      }
    },
    [
      benefitId,
      dispatch,
      employeePublicKey,
      enableNoteModalBeforeCreate,
      enableTerminateAllBeforeCreate,
      hasActiveCardsToTerminate,
      onGenerateCard,
      pendingProvider,
      person?.FullName,
      refreshCards,
    ]
  )

  /**
   * Handle generate modal cancel.
   */
  const handleGenerateCancel = useCallback(() => {
    setGenerateModalOpen(false)
    setPendingProvider(null)
  }, [])

  /**
   * Execute the reuse VCN action directly without popup.
   */
  const executeReuseVcn = useCallback(
    (card) => {
      dispatch(
        reuseVirtualCard({
          benefitId,
          cardId: card.userPublicKey,
          cardPublicKey: card.id,
          userPublicKey: employeePublicKey,
          virtualCardType: card.virtualCardType,
          merchantLogId: card.merchantLogId,
        })
      ).then(() => {
        postActivityLog({
          additionalData: {
            Id: benefitId,
            cardId: card.userPublicKey,
            cardPublicKey: card.id,
            UserPublicKey: employeePublicKey,
            virtualCardType: card.virtualCardType,
            merchantLogId: card.merchantLogId,
          },
          action: ActivityLogAction.REUSE_VCN,
          description: `reuse VCN for ${person?.FullName}`,
          targetUserId: employeePublicKey,
        })

        // Refresh cards list after successful reuse
        refreshCards()
      })
    },
    [benefitId, dispatch, employeePublicKey, person?.FullName, refreshCards]
  )

  /**
   * Handle reuse VCN button click.
   * Directly executes reuse without confirmation popup.
   */
  const handleReuseClick = useCallback(() => {
    const card = availableCards?.[selectedCard]

    if (!card) {
      toast.error(
        'There was an error trying to reuse the selected virtual card',
        { hideProgressBar: true }
      )
      return
    }

    // Execute reuse directly without popup
    executeReuseVcn(card)
  }, [availableCards, selectedCard, executeReuseVcn])

  return {
    // Feature flags
    enableNoteModalBeforeCreate,
    enableTerminateAllBeforeCreate,

    // Modal states
    generateModalOpen,
    isTerminatingAll,

    // Computed values
    shouldShowTerminateWarning,
    providerAvailability,

    // Handlers
    handleProviderSelect,
    handleGenerateConfirm,
    handleGenerateCancel,
    handleReuseClick,
  }
}

export default useVcnActionsLegacy
